import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnalyzeImport, useApplyImportSuggestions } from '../../hooks/useImportAnalysis'
import type { ImportAnalysis, ImportSelections } from '../../services/negotiationsApi'

interface Props {
  negotiationId: number
  onDismiss: () => void
  onApplied?: () => void
}

export default function ImportAnalysisSheet({ negotiationId, onDismiss, onApplied }: Props) {
  const { t } = useTranslation()
  const analyze = useAnalyzeImport(negotiationId)
  const apply = useApplyImportSuggestions(negotiationId)
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null)
  const [selected, setSelected] = useState({
    title: true,
    parties: new Set<number>(),
    metadata: true,
  })

  const handleAnalyze = () => {
    analyze.mutate(undefined, {
      onSuccess: (data) => {
        setAnalysis(data)
        setSelected(prev => ({
          ...prev,
          parties: new Set(data.parties.map((_, i) => i)),
        }))
      },
    })
  }

  if (!analysis && !analyze.isPending) {
    return (
      <div className="rounded border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
            {t('negotiations.aiImport.title', 'AI Import Analysis')}
          </p>
          <button onClick={onDismiss} className="text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <button
          onClick={handleAnalyze}
          className="mt-2 w-full rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
        >
          {t('negotiations.aiImport.analyze', 'Analyze')}
        </button>
      </div>
    )
  }

  if (analyze.isPending) {
    return (
      <div className="rounded border border-neutral-200 p-4 text-center dark:border-neutral-700">
        <div className="mx-auto h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <p className="mt-2 text-xs text-neutral-500">
          {t('negotiations.aiImport.analyzing', 'Analyzing document...')}
        </p>
      </div>
    )
  }

  if (analyze.isError || (analysis && !analysis.success)) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950">
        <div className="flex items-center justify-between">
          <p className="text-xs text-red-700 dark:text-red-300">{analysis?.error || t('negotiations.aiImport.failed', 'Analysis failed.')}</p>
          <button onClick={onDismiss} className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <button onClick={handleAnalyze} className="mt-1 text-xs font-medium text-red-700 dark:text-red-300 underline">
          {t('negotiations.aiImport.tryAgain', 'Try again')}
        </button>
      </div>
    )
  }

  if (!analysis) return null

  const selectedCount = (selected.title ? 1 : 0) + selected.parties.size + (selected.metadata ? 1 : 0)

  const handleApply = () => {
    const selections: ImportSelections = {}
    if (selected.title && analysis.title) selections.title = analysis.title
    if (selected.parties.size > 0) {
      selections.parties = Array.from(selected.parties).map(i => ({
        name: analysis.parties[i].name,
        email: '',
        role_hint: analysis.parties[i].role_hint,
      }))
    }
    if (selected.metadata && (analysis.key_dates.length > 0 || analysis.financial_terms.length > 0)) {
      selections.metadata = {
        key_dates: analysis.key_dates,
        financial_terms: analysis.financial_terms,
      }
    }
    apply.mutate(selections, { onSuccess: () => onApplied?.() })
  }

  const toggleParty = (i: number) => {
    setSelected(prev => {
      const next = new Set(prev.parties)
      next.has(i) ? next.delete(i) : next.add(i)
      return { ...prev, parties: next }
    })
  }

  return (
    <div className="rounded border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800">
      <div className="border-b border-neutral-200 px-3 py-2 dark:border-neutral-700">
        <h4 className="text-xs font-semibold text-neutral-900 dark:text-neutral-100">
          {t('negotiations.aiImport.title', 'AI Import Analysis')}
        </h4>
      </div>

      <div className="max-h-64 divide-y divide-neutral-100 overflow-y-auto dark:divide-neutral-700">
        {/* Title */}
        {analysis.title && (
          <label className="flex items-center gap-2 px-3 py-2">
            <input type="checkbox" checked={selected.title} onChange={() => setSelected(p => ({ ...p, title: !p.title }))} className="h-3.5 w-3.5 rounded" />
            <div className="min-w-0">
              <span className="text-[10px] uppercase text-neutral-400">{t('negotiations.aiImport.suggestedTitle', 'Title')}</span>
              <p className="truncate text-xs text-neutral-900 dark:text-neutral-100">{analysis.title}</p>
            </div>
          </label>
        )}

        {/* Parties */}
        {analysis.parties.map((party, i) => (
          <label key={i} className="flex items-center gap-2 px-3 py-2">
            <input type="checkbox" checked={selected.parties.has(i)} onChange={() => toggleParty(i)} className="h-3.5 w-3.5 rounded" />
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-neutral-900 dark:text-neutral-100">{party.name}</p>
              <p className="text-[10px] text-neutral-500">{party.role_hint}</p>
            </div>
          </label>
        ))}

        {/* Clauses (read-only in compact view) */}
        {analysis.clauses.length > 0 && (
          <div className="px-3 py-2">
            <span className="text-[10px] uppercase text-neutral-400">
              {t('negotiations.aiImport.clauses', 'Clauses')} ({analysis.clauses.length})
            </span>
            {analysis.clauses.slice(0, 5).map((c, i) => (
              <div key={i} className="mt-1 flex items-center gap-1">
                <span className="rounded bg-neutral-100 px-1 text-[9px] font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">
                  {c.category}
                </span>
                <span className="truncate text-[10px] text-neutral-700 dark:text-neutral-300">{c.title}</span>
                {c.library_matches.length > 0 && (
                  <span className="text-[9px] text-blue-500">&#10003;</span>
                )}
              </div>
            ))}
            {analysis.clauses.length > 5 && (
              <p className="mt-1 text-[10px] text-neutral-400">+{analysis.clauses.length - 5} more</p>
            )}
          </div>
        )}

        {/* Metadata */}
        {(analysis.key_dates.length > 0 || analysis.financial_terms.length > 0) && (
          <label className="flex items-start gap-2 px-3 py-2">
            <input type="checkbox" checked={selected.metadata} onChange={() => setSelected(p => ({ ...p, metadata: !p.metadata }))} className="mt-0.5 h-3.5 w-3.5 rounded" />
            <div className="min-w-0">
              {analysis.key_dates.map((d, i) => (
                <p key={i} className="text-[10px] text-neutral-600 dark:text-neutral-300">{d.description}: {d.date}</p>
              ))}
              {analysis.financial_terms.map((f, i) => (
                <p key={i} className="text-[10px] text-neutral-600 dark:text-neutral-300">{f.description}: {f.amount} {f.currency}</p>
              ))}
            </div>
          </label>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-neutral-200 px-3 py-2 dark:border-neutral-700">
        <button onClick={onDismiss} className="text-[10px] text-neutral-500 hover:text-neutral-700">
          {t('negotiations.aiImport.dismiss', 'Dismiss')}
        </button>
        <button
          onClick={handleApply}
          disabled={selectedCount === 0 || apply.isPending}
          className="rounded bg-blue-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {apply.isPending ? '...' : t('negotiations.aiImport.applySelected', 'Apply ({{count}})', { count: selectedCount })}
        </button>
      </div>
    </div>
  )
}
